<!--
  TreeSelect 树形选择器组件
  基于 element-ui 的 el-select 和 el-tree 封装
  模拟 element-plus 的 el-tree-select 功能
  
  使用示例:
  <TreeSelect
    v-model="selectedValue"
    :data="treeData"
    :props="{ label: 'name', children: 'children', value: 'id' }"
    :multiple="false"
    :checkStrictly="true"
    :filterable="true"
    placeholder="请选择"
    @change="handleChange"
  />
-->
<template>
  <div class="tree-select-wrapper">
    <el-select
      ref="select"
      v-model="selectLabel"
      :placeholder="placeholder"
      :disabled="disabled"
      :clearable="clearable"
      :size="size"
      :multiple="multiple"
      :collapse-tags="collapseTags"
      :filterable="filterable"
      :filter-method="handleFilter"
      popper-class="tree-select-popper"
      @clear="handleClear"
      @remove-tag="handleRemoveTag"
      @visible-change="handleVisibleChange"
    >
      <!-- 用于占位，实际选项由树形控件提供 -->
      <el-option
        v-for="item in selectedOptions"
        :key="item[nodeKey]"
        :label="item[defaultProps.label]"
        :value="item[nodeKey]"
        style="display: none;"
      />
      <!-- 占位选项，防止显示"无数据" -->
      <el-option value="__placeholder__" style="display: none;" />
      <!-- 树形控件 -->
      <el-tree
        ref="tree"
        class="tree-select-tree"
        :data="filteredData"
        :props="defaultProps"
        :node-key="nodeKey"
        :show-checkbox="multiple"
        :check-strictly="checkStrictly"
        :default-expand-all="defaultExpandAll"
        :expand-on-click-node="expandOnClickNode"
        :check-on-click-node="checkOnClickNode"
        :highlight-current="!multiple"
        :filter-node-method="filterNode"
        :accordion="accordion"
        :default-expanded-keys="defaultExpandedKeys"
        @node-click="handleNodeClick"
        @check="handleCheck"
      >
        <template #default="{ node, data }">
          <slot name="default" :node="node" :data="data">
            <span class="tree-node-label">{{ node.label }}</span>
          </slot>
        </template>
      </el-tree>
    </el-select>
  </div>
</template>

<script>
export default {
  name: 'TreeSelect',
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    // 绑定值
    value: {
      type: [String, Number, Array],
      default: () => ''
    },
    // 树形数据
    data: {
      type: Array,
      default: () => []
    },
    // 配置选项
    props: {
      type: Object,
      default: () => ({})
    },
    // 节点唯一标识的属性名
    nodeKey: {
      type: String,
      default: 'id'
    },
    // 占位符
    placeholder: {
      type: String,
      default: '请选择'
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 是否可清空
    clearable: {
      type: Boolean,
      default: true
    },
    // 尺寸
    size: {
      type: String,
      default: ''
    },
    // 是否多选
    multiple: {
      type: Boolean,
      default: false
    },
    // 多选时是否折叠标签
    collapseTags: {
      type: Boolean,
      default: false
    },
    // 是否可搜索
    filterable: {
      type: Boolean,
      default: false
    },
    // 父子节点是否不关联
    checkStrictly: {
      type: Boolean,
      default: false
    },
    // 是否默认展开所有节点
    defaultExpandAll: {
      type: Boolean,
      default: false
    },
    // 是否在点击节点时展开或收缩
    expandOnClickNode: {
      type: Boolean,
      default: true
    },
    // 是否在点击节点时选中节点
    checkOnClickNode: {
      type: Boolean,
      default: false
    },
    // 是否手风琴模式
    accordion: {
      type: Boolean,
      default: false
    },
    // 默认展开的节点的 key 数组
    defaultExpandedKeys: {
      type: Array,
      default: () => []
    },
    // 是否只能选择叶子节点
    leafOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectLabel: this.multiple ? [] : '',
      selectedOptions: [],
      filterText: '',
      filteredData: []
    }
  },
  computed: {
    // 默认配置
    defaultProps() {
      return {
        label: 'label',
        children: 'children',
        disabled: 'disabled',
        isLeaf: 'isLeaf',
        ...this.props
      }
    }
  },
  watch: {
    value: {
      handler(val) {
        this.initValue(val)
      },
      immediate: true
    },
    data: {
      handler(val) {
        this.filteredData = val
        this.$nextTick(() => {
          this.initValue(this.value)
        })
      },
      immediate: true,
      deep: true
    },
    filterText(val) {
      if (this.$refs.tree) {
        this.$refs.tree.filter(val)
      }
    }
  },
  mounted() {
    console.log("==============",this.data);
    
  },
  methods: {
    // 初始化值
    initValue(val) {
      if (!val || (Array.isArray(val) && val.length === 0)) {
        this.selectLabel = this.multiple ? [] : ''
        this.selectedOptions = []
        if (this.$refs.tree) {
          if (this.multiple) {
            this.$refs.tree.setCheckedKeys([])
          } else {
            this.$refs.tree.setCurrentKey(null)
          }
        }
        return
      }

      this.$nextTick(() => {
        if (this.multiple) {
          // 多选模式
          const keys = Array.isArray(val) ? val : [val]
          const nodes = keys.map(key => this.findNode(this.data, key)).filter(Boolean)
          this.selectedOptions = nodes
          this.selectLabel = keys
          if (this.$refs.tree) {
            this.$refs.tree.setCheckedKeys(keys)
          }
        } else {
          // 单选模式
          const node = this.findNode(this.data, val)
          if (node) {
            this.selectedOptions = [node]
            this.selectLabel = val
            if (this.$refs.tree) {
              this.$refs.tree.setCurrentKey(val)
            }
          }
        }
      })
    },

    // 递归查找节点
    findNode(data, key) {
      for (const item of data) {
        if (item[this.nodeKey] === key) {
          return item
        }
        const children = item[this.defaultProps.children]
        if (children && children.length > 0) {
          const found = this.findNode(children, key)
          if (found) return found
        }
      }
      return null
    },

    // 节点点击事件（单选模式）
    handleNodeClick(data, node) {
      if (this.multiple) return
      
      // 如果只能选择叶子节点
      if (this.leafOnly && !node.isLeaf) {
        return
      }

      // 如果节点被禁用
      if (data[this.defaultProps.disabled]) {
        return
      }

      const value = data[this.nodeKey]
      this.selectedOptions = [data]
      this.selectLabel = value
      this.$emit('change', value)
      this.$emit('node-click', data, node)
      
      // 关闭下拉框
      if (this.$refs.select) {
        this.$refs.select.blur()
      }
    },

    // 复选框选中事件（多选模式）
    handleCheck(data, { checkedKeys, checkedNodes }) {
      if (!this.multiple) return

      let keys = checkedKeys
      let nodes = checkedNodes

      // 如果只能选择叶子节点
      if (this.leafOnly) {
        nodes = checkedNodes.filter(node => {
          const children = node[this.defaultProps.children]
          return !children || children.length === 0
        })
        keys = nodes.map(node => node[this.nodeKey])
      }

      this.selectedOptions = nodes
      this.selectLabel = keys
      this.$emit('change', keys)
      this.$emit('check', data, { checkedKeys: keys, checkedNodes: nodes })
    },

    // 清空选择
    handleClear() {
      if (this.multiple) {
        this.selectLabel = []
        this.selectedOptions = []
        if (this.$refs.tree) {
          this.$refs.tree.setCheckedKeys([])
        }
        this.$emit('change', [])
      } else {
        this.selectLabel = ''
        this.selectedOptions = []
        if (this.$refs.tree) {
          this.$refs.tree.setCurrentKey(null)
        }
        this.$emit('change', '')
      }
      this.$emit('clear')
    },

    // 移除标签（多选模式）
    handleRemoveTag(tag) {
      const index = this.selectLabel.indexOf(tag)
      if (index > -1) {
        const newKeys = [...this.selectLabel]
        newKeys.splice(index, 1)
        this.selectLabel = newKeys
        this.selectedOptions = this.selectedOptions.filter(
          item => item[this.nodeKey] !== tag
        )
        if (this.$refs.tree) {
          this.$refs.tree.setCheckedKeys(newKeys)
        }
        this.$emit('change', newKeys)
        this.$emit('remove-tag', tag)
      }
    },

    // 下拉框显示/隐藏
    handleVisibleChange(visible) {
      if (!visible) {
        this.filterText = ''
        this.filteredData = this.data
      }
      this.$emit('visible-change', visible)
    },

    // 搜索过滤
    handleFilter(val) {
      this.filterText = val
      if (!val) {
        this.filteredData = this.data
        return
      }
      this.filteredData = this.filterTreeData(this.data, val)
    },

    // 递归过滤树形数据
    filterTreeData(data, keyword) {
      const result = []
      for (const item of data) {
        const label = item[this.defaultProps.label] || ''
        const children = item[this.defaultProps.children]
        
        if (label.toLowerCase().includes(keyword.toLowerCase())) {
          result.push({ ...item })
        } else if (children && children.length > 0) {
          const filteredChildren = this.filterTreeData(children, keyword)
          if (filteredChildren.length > 0) {
            result.push({
              ...item,
              [this.defaultProps.children]: filteredChildren
            })
          }
        }
      }
      return result
    },

    // 树节点过滤方法
    filterNode(value, data) {
      if (!value) return true
      const label = data[this.defaultProps.label] || ''
      return label.toLowerCase().includes(value.toLowerCase())
    },

    // 获取选中的节点
    getCheckedNodes(leafOnly = false, includeHalfChecked = false) {
      if (this.$refs.tree) {
        return this.$refs.tree.getCheckedNodes(leafOnly, includeHalfChecked)
      }
      return []
    },

    // 获取选中的节点 key
    getCheckedKeys(leafOnly = false) {
      if (this.$refs.tree) {
        return this.$refs.tree.getCheckedKeys(leafOnly)
      }
      return []
    },

    // 设置选中的节点
    setCheckedNodes(nodes) {
      if (this.$refs.tree) {
        this.$refs.tree.setCheckedNodes(nodes)
      }
    },

    // 设置选中的节点 key
    setCheckedKeys(keys) {
      if (this.$refs.tree) {
        this.$refs.tree.setCheckedKeys(keys)
      }
    },

    // 获取当前选中节点（单选模式）
    getCurrentNode() {
      if (this.$refs.tree) {
        return this.$refs.tree.getCurrentNode()
      }
      return null
    },

    // 获取当前选中节点的 key（单选模式）
    getCurrentKey() {
      if (this.$refs.tree) {
        return this.$refs.tree.getCurrentKey()
      }
      return null
    },

    // 设置当前选中节点（单选模式）
    setCurrentNode(node) {
      if (this.$refs.tree) {
        this.$refs.tree.setCurrentNode(node)
      }
    },

    // 设置当前选中节点的 key（单选模式）
    setCurrentKey(key) {
      if (this.$refs.tree) {
        this.$refs.tree.setCurrentKey(key)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.tree-select-wrapper {
  width: 100%;
  
  ::v-deep .el-select {
    width: 100%;
  }
}

.tree-select-tree {
  padding: 4px 0;
  max-height: 300px;
  overflow-y: auto;
  
  ::v-deep .el-tree-node__content {
    height: 32px;
    line-height: 32px;
  }
  
  .tree-node-label {
    font-size: 14px;
  }
}
</style>

<style lang="scss">
// 全局样式，用于下拉框弹出层
.tree-select-popper {
  .el-scrollbar {
    .el-select-dropdown__wrap {
      max-height: 350px;
    }
  }
  
  .el-select-dropdown__item {
    display: none;
  }
}
</style>